import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error(t('fillAllFields'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('passwordsDontMatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Modpas chanje avèk siksè!');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Erè. Tanpri eseye ankò.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-mrc-navy border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Ap verifye token...</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 shadow-lg text-center">
            <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-mrc-navy mb-4">
              Lyen Pa Valid
            </h1>
            <p className="text-muted-foreground mb-6">
              Lyen reset modpas sa a pa valid oswa li ekspire. Tanpri fè yon nouvo demann.
            </p>

            <Link to="/forgot-password">
              <Button className="w-full bg-mrc-navy hover:bg-mrc-navy/90 text-white">
                Mande Nouvo Lyen
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 shadow-lg text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-mrc-navy mb-4">
              Modpas Chanje!
            </h1>
            <p className="text-muted-foreground mb-6">
              Modpas ou chanje avèk siksè. Ou ka konekte kounye a ak nouvo modpas ou.
            </p>

            <Link to="/login">
              <Button className="w-full bg-mrc-navy hover:bg-mrc-navy/90 text-white py-6 text-lg">
                Ale nan Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 shadow-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_mwenkomerce/artifacts/xt1gnrb8_IMG_1789.png" 
              alt="MRC Boutique Logo" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl font-bold text-mrc-navy">
              Reset Modpas
            </h1>
            <p className="text-muted-foreground mt-2">
              Antre nouvo modpas pou <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password">Nouvo Modpas</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Konfime Nouvo Modpas</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 pl-10"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-mrc-navy hover:bg-mrc-navy/90 text-white py-6 text-lg font-semibold uppercase tracking-wider rounded-full"
              data-testid="reset-btn"
            >
              {loading ? 'Ap chanje...' : 'Chanje Modpas'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
